import React, { useState } from 'react';
import { X, Building, Mail, User, ShieldCheck, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCurrentUser } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useCreateNode } from '../../../hooks/useNode';
import { DEFAULT_ENABLED_MODULES } from '../../../constants';
import { getToolCtaCopy, getToolFormPlaceholders } from '../../../lib/toolCopy';
import { ensurePendingToolDraft, loadToolDraft } from '../../../lib/toolState';
import { TOOL_EVENTS, track } from '../../../lib/analytics';

interface ToolClaimOrgModalProps {
  isOpen: boolean;
  onClose: () => void;
  toolTitle: string;
  toolSlug: string;
  toolCategory: string;
  defaultOrgName?: string;
}

export const ToolClaimOrgModal: React.FC<ToolClaimOrgModalProps> = ({
  isOpen,
  onClose,
  toolTitle,
  toolSlug,
  toolCategory,
  defaultOrgName = ''
}) => {
  const navigate = useNavigate();
  const { isSignedIn, isBackendSynced, memberships } = useCurrentUser();
  const { switchOrg } = useTenant();
  const createNode = useCreateNode();

  const [orgName, setOrgName] = useState(() => {
    try {
      return defaultOrgName || localStorage.getItem('orgsets_lead_org') || '';
    } catch {
      return defaultOrgName || '';
    }
  });

  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem('orgsets_lead_email') || '';
    } catch {
      return '';
    }
  });

  const [adminName, setAdminName] = useState(() => {
    try {
      return localStorage.getItem('orgsets_lead_name') || '';
    } catch {
      return '';
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const copy = getToolCtaCopy({ slug: toolSlug, title: toolTitle, category: toolCategory });
  const placeholders = getToolFormPlaceholders({ slug: toolSlug, title: toolTitle, category: toolCategory });

  if (!isOpen) return null;

  const mapTemplate = (category: string, slug: string): string => {
    const s = slug.toLowerCase();
    const c = (category || '').toLowerCase();
    if (s.includes('swim') || s.includes('sport') || s.includes('bracket') || s.includes('round-robin') || s.includes('team')) {
      return 'SPORTS_LEAGUE';
    }
    if (s.includes('hoa') || s.includes('neighborhood') || s.includes('community-land')) {
      return 'HOA';
    }
    if (s.includes('church') || s.includes('worship') || s.includes('ministry')) {
      return 'CHURCH';
    }
    if (s.includes('union') || s.includes('guild')) {
      return 'UNION';
    }
    if (s.includes('scout') || s.includes('troop') || s.includes('club') || c === 'community') {
      return 'CLUB';
    }
    return 'NONPROFIT';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgName.trim()) {
      toast.error('Please enter an organization name.');
      return;
    }
    if (!isSignedIn && (!email.trim() || !email.includes('@'))) {
      toast.error('Please enter a valid admin email address.');
      return;
    }

    const trimmedOrg = orgName.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName = adminName.trim();
    const slug = trimmedOrg.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `${toolSlug}-${Date.now().toString(36)}`;
    const template = mapTemplate(toolCategory, toolSlug);
    const existingDraft = loadToolDraft(toolSlug);

    // Link the tool result to the account flow. This pointer is what lets the
    // dashboard say "your work is still here" after signup.
    ensurePendingToolDraft(toolSlug, toolTitle, trimmedOrg, 'claim_modal');

    try {
      if (trimmedOrg) localStorage.setItem('orgsets_lead_org', trimmedOrg);
      if (trimmedEmail) localStorage.setItem('orgsets_lead_email', trimmedEmail);
      if (trimmedName) localStorage.setItem('orgsets_lead_name', trimmedName);

      // Only queue an org when the account does not already have a workspace.
      if (!isSignedIn || memberships.length === 0) {
        const pendingHubData = {
          name: trimmedOrg,
          slug,
          template,
          tier: 'LOCAL',
          visibility: 'PUBLIC',
          modules: DEFAULT_ENABLED_MODULES,
          sourceTool: toolSlug,
          toolTitle
        };
        localStorage.setItem('pending_hub_creation', JSON.stringify(pendingHubData));
      }
    } catch {}

    track(TOOL_EVENTS.claimSubmit, {
      tool_slug: toolSlug,
      tool_segment: copy.segment,
      signed_in: isSignedIn,
      has_memberships: memberships.length > 0,
      has_draft: Boolean(existingDraft?.fields.length),
    });

    // Case 1: User is already signed in with an existing workspace.
    // There is nothing to provision; keep the draft and return them to it.
    if (isSignedIn && isBackendSynced && memberships.length > 0) {
      toast.success('Draft saved on this device. Continue it from your dashboard.');
      onClose();
      navigate('/dashboard');
      return;
    }

    // Case 2: User is signed in and needs their first workspace.
    if (isSignedIn && isBackendSynced) {
      setIsSubmitting(true);
      try {
        const created: any = await createNode.mutateAsync({
          name: trimmedOrg,
          slug,
          template,
          tier: 'LOCAL',
          visibility: 'PUBLIC',
          modules: [...DEFAULT_ENABLED_MODULES]
        });
        localStorage.removeItem('pending_hub_creation');
        toast.success(`Organization "${trimmedOrg}" created! You are the registered Admin.`);
        onClose();
        const targetSlug = created?.slug || slug;
        switchOrg(targetSlug, '/dashboard');
      } catch (err: any) {
        console.error('Failed to create org directly:', err);
        toast.error(err?.response?.data?.message || 'Organization name already taken or connection error.');
        onClose();
        navigate('/create-org');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Case 3: Unauthenticated user — hand off to Sign Up.
    onClose();
    const signUpUrl = trimmedEmail
      ? `/sign-up?redirect_url=/dashboard&source=tool&slug=${encodeURIComponent(toolSlug)}&email=${encodeURIComponent(trimmedEmail)}`
      : `/sign-up?redirect_url=/dashboard&source=tool&slug=${encodeURIComponent(toolSlug)}`;
    navigate(signUpUrl);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm print:hidden animate-in fade-in duration-150">
      <div className="bg-white dark:bg-[#0A0A0A] border-[8px] border-black dark:border-white w-full max-w-lg shadow-[16px_16px_0px_#0044FF] dark:shadow-[16px_16px_0px_#FFD600] relative p-6 md:p-8">
        
        {/* Dismiss button */}
        <button 
          onClick={onClose}
          type="button"
          className="absolute top-4 right-4 p-2 bg-black text-white dark:bg-white dark:text-black border-2 border-black dark:border-white hover:bg-[#0044FF] hover:text-white dark:hover:bg-[#FFD600] dark:hover:text-black transition-colors cursor-pointer"
          aria-label="Close"
        >
          <X size={18} strokeWidth={3} />
        </button>

        <div>
          {/* Header Pill */}
          <div className="mb-3 inline-flex items-center gap-2 bg-[#0044FF] text-white px-3 py-1 font-mono uppercase tracking-widest font-black text-xs border-2 border-black dark:border-white shadow-[3px_3px_0px_#000]">
            <Sparkles size={12} className="shrink-0" />
            <span>{isSignedIn ? 'Save Your Work' : 'Create Free Workspace'}</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-black font-display uppercase tracking-tight text-black dark:text-white leading-[0.95] mb-2">
            {copy.claimHeadline}
          </h2>
          
          <p className="text-xs sm:text-sm font-bold text-gray-700 dark:text-gray-300 leading-relaxed mb-5">
            {copy.claimBody}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block font-mono text-[11px] uppercase font-black tracking-wider text-black dark:text-white mb-1">
                Organization / Team Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input 
                  type="text"
                  required
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="e.g. Austin Blue Fins Swim Club"
                  className="w-full border-3 border-black dark:border-white bg-white dark:bg-black font-mono text-sm p-3 text-black dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-[#0044FF] dark:focus:border-[#FFD600]"
                />
                <Building className="absolute right-3 top-3 text-gray-400 pointer-events-none" size={18} />
              </div>
            </div>

            {!isSignedIn && (
              <>
                <div>
                  <label className="block font-mono text-[11px] uppercase font-black tracking-wider text-black dark:text-white mb-1">
                    Your Full Name <span className="text-gray-400 font-normal text-[10px]">(Optional)</span>
                  </label>
                  <div className="relative">
                    <input 
                      type="text"
                      value={adminName}
                      onChange={(e) => setAdminName(e.target.value)}
                      placeholder="e.g. Coach Sarah Miller"
                      className="w-full border-3 border-black dark:border-white bg-white dark:bg-black font-mono text-sm p-3 text-black dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-[#0044FF] dark:focus:border-[#FFD600]"
                    />
                    <User className="absolute right-3 top-3 text-gray-400 pointer-events-none" size={18} />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[11px] uppercase font-black tracking-wider text-black dark:text-white mb-1">
                    Your Admin Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input 
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="sarah@aquatics.org"
                      className="w-full border-3 border-black dark:border-white bg-white dark:bg-black font-mono text-sm p-3 text-black dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-[#0044FF] dark:focus:border-[#FFD600]"
                    />
                    <Mail className="absolute right-3 top-3 text-gray-400 pointer-events-none" size={18} />
                  </div>
                </div>
              </>
            )}

            {/* Owner Guarantee Callout */}
            <div className="bg-[#FFD600]/15 dark:bg-[#FFD600]/10 border-2 border-black dark:border-white p-3.5 space-y-1.5 text-xs text-black dark:text-white font-bold">
              <div className="flex items-center gap-2 font-black uppercase text-[11px] tracking-wider text-amber-900 dark:text-[#FFD600]">
                <ShieldCheck size={16} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
                <span>Verified Owner &amp; Admin Guarantee</span>
              </div>
              <p className="text-[11px] leading-relaxed text-gray-700 dark:text-gray-300 font-medium">
                You will be the sole registered <strong>Administrator &amp; Owner</strong>. Your tool inputs stay on this device now, and your dashboard will link you straight back to them after signup.
              </p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0044FF] hover:bg-blue-700 text-white font-black uppercase tracking-wider py-4 text-base border-[4px] border-black dark:border-white transition-colors shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#FFD600] cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span>Provisioning Workspace...</span>
                ) : (
                  <>
                    <span>{isSignedIn ? 'Save to My Workspace' : copy.claimCta}</span>
                    <ArrowRight size={18} strokeWidth={3} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
